import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { 
  GraduationCap, BookOpen, LogOut, Upload, Users, FileText, 
  BarChart3, Plus, Loader2, CheckCircle, ChevronRight, FileUp, Key, Trash2, Shield
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TeacherDashboard({ user, logout }) {
  const [stats, setStats] = useState(null);
  const [tests, setTests] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingTestId, setDeletingTestId] = useState(null);
  
  // Form state for creating test
  const [testName, setTestName] = useState("");
  const [testGrade, setTestGrade] = useState("");
  const [testYear, setTestYear] = useState(new Date().getFullYear().toString());
  const [testSheetFile, setTestSheetFile] = useState(null);
  const [answerKeyFile, setAnswerKeyFile] = useState(null);
  const [answerKeyMode, setAnswerKeyMode] = useState("pdf"); // "pdf" or "manual"
  const [totalQuestions, setTotalQuestions] = useState(24);
  const [answers, setAnswers] = useState({});
  const [concepts, setConcepts] = useState({});
  
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, testsRes, studentsRes] = await Promise.all([
        axios.get(`${API}/stats/overview`),
        axios.get(`${API}/tests`).catch(() => ({ data: [] })),
        axios.get(`${API}/teacher/students`)
      ]);
      setStats(statsRes.data);
      setTests(testsRes.data || []);
      setStudents(studentsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTestSheetChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error("Please upload a PDF file for the test sheet");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File too large. Maximum 5MB allowed.");
        return;
      }
      setTestSheetFile(file);
    }
  };

  const handleAnswerKeyFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error("Please upload a PDF file for the answer key");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File too large. Maximum 5MB allowed.");
        return;
      }
      setAnswerKeyFile(file);
    }
  };

  const handleAnswerChange = (questionNum, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionNum]: value.toUpperCase()
    }));
  };

  const handleConceptChange = (questionNum, value) => {
    setConcepts(prev => ({
      ...prev,
      [questionNum]: value
    }));
  };

  const handleCreateTest = async () => {
    if (!testName.trim()) {
      toast.error("Please enter a test name");
      return;
    }
    if (!testGrade) {
      toast.error("Please select a grade level");
      return;
    }
    if (!testSheetFile) {
      toast.error("Please upload the test questions PDF");
      return;
    }
    
    // Validate answer key based on mode
    if (answerKeyMode === "pdf" && !answerKeyFile) {
      toast.error("Please upload the answer key PDF");
      return;
    }
    if (answerKeyMode === "manual") {
      const filledAnswers = Object.keys(answers).filter(k => answers[k]);
      if (filledAnswers.length === 0) {
        toast.error("Please enter at least one answer");
        return;
      }
    }

    setCreating(true);
    try {
      const formData = new FormData();
      formData.append("name", testName.trim());
      formData.append("grade_level", testGrade);
      formData.append("year", testYear);
      formData.append("total_questions", totalQuestions.toString());
      formData.append("test_sheet", testSheetFile);
      formData.append("answer_key_mode", answerKeyMode);
      
      if (answerKeyMode === "pdf") {
        formData.append("answer_key_pdf", answerKeyFile);
      } else {
        formData.append("answers_json", JSON.stringify(answers));
        formData.append("concepts_json", JSON.stringify(concepts));
      }

      const response = await axios.post(`${API}/tests`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (answerKeyMode === "pdf") {
        toast.success(`Test created! AI extracted ${response.data.total_questions} answers.`);
      } else {
        toast.success("Test created successfully!");
      }
      setCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create test");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTest = async (testId) => {
    setDeletingTestId(testId);
    try {
      const response = await axios.delete(`${API}/tests/${testId}`);
      toast.success(`Test deleted. ${response.data.files_deleted} files removed.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete test");
    } finally {
      setDeletingTestId(null);
    }
  };

  const resetForm = () => {
    setTestName("");
    setTestGrade("");
    setTestYear(new Date().getFullYear().toString());
    setTestSheetFile(null);
    setAnswerKeyFile(null);
    setAnswerKeyMode("pdf");
    setTotalQuestions(24);
    setAnswers({});
    setConcepts({});
  };

  const conceptOptions = [
    "Algebra", "Geometry", "Logic", "Number Theory", 
    "Combinatorics", "Arithmetic", "Word Problems", "Patterns"
  ];

  if (loading) {
    return (
      <div className="min-h-screen graph-paper-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen graph-paper-bg">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                MathTrack
              </h1>
              <p className="text-sm text-gray-500">Teacher Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-medium text-gray-900">{user?.display_name || "Teacher"}</p>
              <p className="text-sm text-gray-500">{user?.role}</p>
            </div>
            {user?.is_admin && (
              <Button 
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin")}
                className="gap-2"
              >
                <Shield className="w-4 h-4" />
                Admin
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="card-hover" data-testid="total-students-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Total Students</p>
                    <p className="text-3xl font-bold text-gray-900 font-mono">
                      {stats?.total_students || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="card-hover" data-testid="total-submissions-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Total Submissions</p>
                    <p className="text-3xl font-bold text-gray-900 font-mono">
                      {stats?.total_submissions || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="card-hover" data-testid="total-tests-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Tests Created</p>
                    <p className="text-3xl font-bold text-gray-900 font-mono">
                      {tests.length || stats?.total_answer_keys || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="tests" className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="tests" data-testid="tests-tab">
              <BookOpen className="w-4 h-4 mr-2" />
              Tests
            </TabsTrigger>
            <TabsTrigger value="students" data-testid="students-tab">
              <Users className="w-4 h-4 mr-2" />
              Students
            </TabsTrigger>
            <TabsTrigger value="submissions" data-testid="submissions-tab">
              <FileText className="w-4 h-4 mr-2" />
              Recent Submissions
            </TabsTrigger>
          </TabsList>

          {/* Tests Tab */}
          <TabsContent value="tests">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Tests & Answer Keys
                </CardTitle>
                <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="create-test-btn"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Create Test
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Create New Test
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-6 pt-4">
                      {/* Basic Info */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Test Name *
                          </label>
                          <Input
                            placeholder="e.g., Math Kangaroo 2024 Level 5-6"
                            value={testName}
                            onChange={(e) => setTestName(e.target.value)}
                            data-testid="test-name-input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Grade Level *
                          </label>
                          <Select value={testGrade} onValueChange={setTestGrade}>
                            <SelectTrigger data-testid="grade-level-select">
                              <SelectValue placeholder="Select grade" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Pre-Ecolier">Pre-Ecolier (1-2)</SelectItem>
                              <SelectItem value="Ecolier">Ecolier (3-4)</SelectItem>
                              <SelectItem value="Benjamin">Benjamin (5-6)</SelectItem>
                              <SelectItem value="Cadet">Cadet (7-8)</SelectItem>
                              <SelectItem value="Junior">Junior (9-10)</SelectItem>
                              <SelectItem value="Student">Student (11-12)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Year
                          </label>
                          <Input
                            type="number"
                            value={testYear}
                            onChange={(e) => setTestYear(e.target.value)}
                            data-testid="year-input"
                          />
                        </div>
                      </div>

                      {/* Test Questions PDF Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <FileUp className="w-4 h-4 inline mr-1" />
                          Test Questions (PDF) *
                        </label>
                        <label className={`dropzone ${testSheetFile ? 'border-green-500 bg-green-50' : ''}`}>
                          <input
                            type="file"
                            accept="application/pdf"
                            onChange={handleTestSheetChange}
                            className="hidden"
                            data-testid="test-sheet-input"
                          />
                          {testSheetFile ? (
                            <div className="flex items-center gap-3 text-green-600">
                              <CheckCircle className="w-8 h-8" />
                              <div className="text-left">
                                <p className="font-medium">{testSheetFile.name}</p>
                                <p className="text-sm text-gray-500">Click to change</p>
                              </div>
                            </div>
                          ) : (
                            <div className="text-gray-500">
                              <FileUp className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                              <p className="font-medium">Upload test questions PDF</p>
                              <p className="text-sm">Max 5MB</p>
                            </div>
                          )}
                        </label>
                      </div>

                      {/* Answer Key Mode Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          <Key className="w-4 h-4 inline mr-1" />
                          Answer Key *
                        </label>
                        <RadioGroup 
                          value={answerKeyMode} 
                          onValueChange={setAnswerKeyMode}
                          className="flex gap-4"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="pdf" id="answer-pdf" />
                            <Label htmlFor="answer-pdf" className="cursor-pointer">
                              Upload Answer Key PDF (AI extracts answers)
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="manual" id="answer-manual" />
                            <Label htmlFor="answer-manual" className="cursor-pointer">
                              Enter answers manually
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {/* Answer Key PDF Upload */}
                      {answerKeyMode === "pdf" && (
                        <div>
                          <label className={`dropzone ${answerKeyFile ? 'border-green-500 bg-green-50' : ''}`}>
                            <input
                              type="file"
                              accept="application/pdf"
                              onChange={handleAnswerKeyFileChange}
                              className="hidden"
                              data-testid="answer-key-input"
                            />
                            {answerKeyFile ? (
                              <div className="flex items-center gap-3 text-green-600">
                                <CheckCircle className="w-8 h-8" />
                                <div className="text-left">
                                  <p className="font-medium">{answerKeyFile.name}</p>
                                  <p className="text-sm text-gray-500">Click to change</p>
                                </div>
                              </div>
                            ) : (
                              <div className="text-gray-500">
                                <Key className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                                <p className="font-medium">Upload answer key PDF</p>
                                <p className="text-sm">AI will extract all correct answers automatically</p>
                              </div>
                            )}
                          </label>
                          <p className="text-xs text-gray-500 mt-2">
                            The AI will read the answer key PDF and extract all correct answers with their question numbers.
                          </p>
                        </div>
                      )}

                      {/* Manual Answer Entry */}
                      {answerKeyMode === "manual" && (
                        <>
                          {/* Number of Questions */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Total Questions
                            </label>
                            <Input
                              type="number"
                              min="1"
                              max="100"
                              value={totalQuestions}
                              onChange={(e) => setTotalQuestions(parseInt(e.target.value) || 24)}
                              data-testid="total-questions-input"
                            />
                          </div>

                          {/* Answer Key Entry */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Answer Key (Enter correct answers)
                            </label>
                            <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                              <div className="grid grid-cols-4 gap-3">
                                {Array.from({ length: totalQuestions }, (_, i) => i + 1).map((num) => (
                                  <div key={num} className="flex items-center gap-2">
                                    <span className="text-sm font-mono text-gray-600 w-6">{num}.</span>
                                    <Input
                                      className="w-14 text-center font-mono uppercase"
                                      maxLength={1}
                                      placeholder="?"
                                      value={answers[num] || ""}
                                      onChange={(e) => handleAnswerChange(num, e.target.value)}
                                      data-testid={`answer-${num}`}
                                    />
                                    <Select 
                                      value={concepts[num] || ""} 
                                      onValueChange={(v) => handleConceptChange(num, v)}
                                    >
                                      <SelectTrigger className="w-28 text-xs">
                                        <SelectValue placeholder="Concept" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {conceptOptions.map(c => (
                                          <SelectItem key={c} value={c}>{c}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      <Button
                        onClick={handleCreateTest}
                        disabled={creating || !testName.trim() || !testGrade || !testSheetFile || (answerKeyMode === "pdf" && !answerKeyFile)}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                        data-testid="confirm-create-btn"
                      >
                        {creating ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            {answerKeyMode === "pdf" ? "Processing with AI..." : "Creating Test..."}
                          </>
                        ) : (
                          <>
                            <Plus className="w-5 h-5 mr-2" />
                            Create Test
                          </>
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {tests.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No tests yet. Create one to get started.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Grade Level</TableHead>
                        <TableHead>Year</TableHead>
                        <TableHead>Questions</TableHead>
                        <TableHead>Files</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="w-20">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tests.map((test) => (
                        <TableRow 
                          key={test.test_id} 
                          data-testid={`test-${test.test_id}`}
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => navigate(`/test/${test.test_id}`)}
                        >
                          <TableCell className="font-medium">
                            <span className="text-purple-600 hover:underline">
                              {test.name}
                            </span>
                          </TableCell>
                          <TableCell>{test.grade_level}</TableCell>
                          <TableCell className="font-mono">{test.year}</TableCell>
                          <TableCell className="font-mono">{test.total_questions}</TableCell>
                          <TableCell>
                            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                              {test.test_sheet_url && (
                                <a 
                                  href={`${BACKEND_URL}${test.test_sheet_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-purple-600 hover:underline flex items-center gap-1 text-sm"
                                >
                                  <FileText className="w-3 h-3" />
                                  PDF
                                </a>
                              )}
                              {test.answer_key_url && (
                                <a 
                                  href={`${BACKEND_URL}${test.answer_key_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-green-600 hover:underline flex items-center gap-1 text-sm"
                                >
                                  <Key className="w-3 h-3" />
                                  PDF
                                </a>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-500">
                            {new Date(test.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  disabled={deletingTestId === test.test_id}
                                >
                                  {deletingTestId === test.test_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Test?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete &quot;{test.name}&quot; including all uploaded PDFs and student submissions. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteTest(test.test_id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                  All Students
                </CardTitle>
              </CardHeader>
              <CardContent>
                {students.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No students have signed up yet.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead>Submissions</TableHead>
                        <TableHead>Avg Score</TableHead>
                        <TableHead>Top Weakness</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {students.map((student) => (
                        <TableRow key={student.user_id} data-testid={`student-${student.user_id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <GraduationCap className="w-4 h-4 text-blue-600" />
                              </div>
                              <span className="font-medium">{student.display_name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="font-mono">{student.stats?.submissions || 0}</TableCell>
                          <TableCell>
                            <span className={`font-mono ${
                              student.stats?.average >= 70 ? 'text-green-600' :
                              student.stats?.average >= 50 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {student.stats?.average || 0}%
                            </span>
                          </TableCell>
                          <TableCell>{student.stats?.top_weakness || "None"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Submissions Tab */}
          <TabsContent value="submissions">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Recent Submissions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!stats?.recent_submissions || stats.recent_submissions.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No submissions yet.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {stats.recent_submissions.map((sub) => (
                      <button
                        key={sub.submission_id}
                        onClick={() => navigate(`/submission/${sub.submission_id}`)}
                        className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        data-testid={`submission-${sub.submission_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            sub.percentage >= 70 ? 'bg-green-100 text-green-600' :
                            sub.percentage >= 50 ? 'bg-yellow-100 text-yellow-600' :
                            'bg-red-100 text-red-600'
                          }`}>
                            <span className="font-bold font-mono">{sub.percentage}%</span>
                          </div>
                          <div className="text-left">
                            <p className="font-medium text-gray-900">{sub.student_display_name || "Student"}</p>
                            <p className="text-sm text-gray-500">
                              {sub.answer_key_name || "Test"} • {sub.score}/{sub.total} correct
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-500">
                            {new Date(sub.created_at).toLocaleDateString()}
                          </span>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
